// Device-side session cleanup (2026-10-06): logout, tenant change, access loss.
//
// What lives on the device and how it is cleaned:
//   * React state                 — cleared by App (clearFirmState) BEFORE anything here runs,
//                                   so firm data disappears from the screen immediately.
//   * firmassist_offline IndexedDB — firm-scoped cache (keys are uid+firmId) + sync queue;
//                                   cleared here (whole cache store: it only ever holds firm data).
//   * Firestore persistence        — persistentLocalCache + persistentMultipleTabManager.
//                                   The SDK can NOT clear selectively by firm: the only API
//                                   is clearIndexedDbPersistence (whole local cache), allowed
//                                   only before start or after terminate(). After terminate()
//                                   this tab's Firestore instance is unusable, so a (guarded)
//                                   reload always follows. Other tabs holding the database
//                                   either terminate themselves on the delete's versionchange
//                                   event (this SDK does) or are asked to via the session
//                                   channel; a still-blocked delete times out and is retried
//                                   at the next start-up (PERSISTENCE_CLEAR_FLAG, capped).
//   * local/sessionStorage         — FirmAssist firm/auth-bound keys only (see clearFirmBrowserStorage).
// Server data is never touched. Personal Ratio/research data lives server-side and is
// never deleted; its only local key (a dismissed first-use note) is a preference and is kept.
import { SESSION_EVENTS } from './sessionChannel';

export const PERSISTENCE_CLEAR_FLAG = 'firmassist.persistenceClearPending';
export const DEVICE_TENANT_KEY = 'firmassist.deviceTenant';
export const RELOAD_LOG_KEY = 'firmassist.sessionReloads';
export const RECOVERY_ERROR_KEY = 'firmassist.authRecoveryError';
export const MAX_STARTUP_CLEAR_ATTEMPTS = 3;
export const PERSISTENCE_CLEAR_TIMEOUT_MS = 6000;
const RELOAD_WINDOW_MS = 30000;
const MAX_RELOADS_IN_WINDOW = 2;

const win = () => (typeof window !== 'undefined' ? window : undefined);
const ls = {
  get: (k) => { try { return win().localStorage.getItem(k); } catch (_) { return null; } },
  set: (k, v) => { try { win().localStorage.setItem(k, v); } catch (_) { /* ignore */ } },
  del: (k) => { try { win().localStorage.removeItem(k); } catch (_) { /* ignore */ } },
};
const ss = {
  get: (k) => { try { return win().sessionStorage.getItem(k); } catch (_) { return null; } },
  set: (k, v) => { try { win().sessionStorage.setItem(k, v); } catch (_) { /* ignore */ } },
  del: (k) => { try { win().sessionStorage.removeItem(k); } catch (_) { /* ignore */ } },
};

// Non-reversible marker of which tenant's data this device's caches may hold
// (only equality matters — no raw uid / firmId is written to storage).
export const tenantFingerprint = (tenant) => {
  if (!tenant) return null;
  let h = 0x811c9dc5;
  for (let i = 0; i < tenant.length; i += 1) { h ^= tenant.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; }
  return `t${h.toString(16)}`;
};
export const getDeviceTenant = () => ls.get(DEVICE_TENANT_KEY);
export const setDeviceTenant = (tenant) => ls.set(DEVICE_TENANT_KEY, tenantFingerprint(tenant));
export const clearDeviceTenant = () => ls.del(DEVICE_TENANT_KEY);

// Which cleanup (if any) the device needs, given a CONFIRMED session outcome.
//   ACTIVE for tenant T:  device holds another tenant's data → TENANT_CHANGED; else nothing.
//   NO_FIRM / SUSPENDED / FIRM_UNAVAILABLE: device holds tenant data → FIRM_ACCESS_LOST.
// The marker is removed before the reload, so the next start cannot loop.
export function cleanupNeeded({ status, tenant, deviceTenant }) {
  if (!deviceTenant) return null;
  if (status === 'ACTIVE') return deviceTenant === tenantFingerprint(tenant) ? null : SESSION_EVENTS.TENANT_CHANGED;
  if (status === 'NO_FIRM' || status === 'SUSPENDED' || status === 'FIRM_UNAVAILABLE') return SESSION_EVENTS.FIRM_ACCESS_LOST;
  return null;
}

const readFlag = () => { try { return JSON.parse(ls.get(PERSISTENCE_CLEAR_FLAG) || 'null'); } catch (_) { return null; } };
export const isPersistenceClearPending = () => !!readFlag();

const withTimeout = (p, ms) => new Promise((resolve, reject) => {
  const t = setTimeout(() => reject(Object.assign(new Error('timeout'), { code: 'timeout' })), ms);
  Promise.resolve(p).then((v) => { clearTimeout(t); resolve(v); }, (e) => { clearTimeout(t); reject(e); });
});

// terminate() then clearIndexedDbPersistence(). Never throws.
// → { cleared: true } | { cleared: false, reason: 'failed-precondition'|'blocked'|'terminate-failed'|'error' }
export async function clearFirestorePersistence({ db, terminate, clearIndexedDbPersistence, timeoutMs = PERSISTENCE_CLEAR_TIMEOUT_MS }) {
  const prev = readFlag();
  ls.set(PERSISTENCE_CLEAR_FLAG, JSON.stringify({ at: Date.now(), attempts: prev ? prev.attempts || 0 : 0 }));
  try { await terminate(db); } catch (e) { return { cleared: false, reason: 'terminate-failed' }; }
  try {
    await withTimeout(clearIndexedDbPersistence(db), timeoutMs);
    ls.del(PERSISTENCE_CLEAR_FLAG);
    return { cleared: true };
  } catch (e) {
    const code = e && e.code;
    const reason = code === 'failed-precondition' ? 'failed-precondition' : code === 'timeout' ? 'blocked' : 'error';
    // Flag stays set: the next start-up retries before Firestore starts.
    return { cleared: false, reason };
  }
}

// Start-up retry of a cleanup that could not finish (another tab held the
// database). Called once, at module load, before the Firestore instance has
// started — the only other moment clearIndexedDbPersistence is allowed.
// Capped so a permanently blocked database can never cause repeated attempts.
export function startupPersistenceClear({ db, clearIndexedDbPersistence, post }) {
  const flag = readFlag();
  if (!flag) return { attempted: false };
  const attempts = (flag.attempts || 0) + 1;
  if (attempts > MAX_STARTUP_CLEAR_ATTEMPTS) { ls.del(PERSISTENCE_CLEAR_FLAG); return { attempted: false, gaveUp: true }; }
  ls.set(PERSISTENCE_CLEAR_FLAG, JSON.stringify({ ...flag, attempts }));
  try {
    if (post) post(SESSION_EVENTS.PERSISTENCE_RESET);
    Promise.resolve(clearIndexedDbPersistence(db)).then(() => ls.del(PERSISTENCE_CLEAR_FLAG), () => { /* retried next start (capped) */ });
  } catch (_) { return { attempted: false }; }
  return { attempted: true, attempts };
}

// FirmAssist firm- or auth-bound browser storage. Kept deliberately:
//   ratio_first_use_note_dismissed (UI preference), the per-day AI usage
//   counters (docIntel_/contractReview_/precedent_ — not firm data), and this
//   module's own bookkeeping keys.
export function clearFirmBrowserStorage(uid, { logout = false } = {}) {
  ls.del('firmassist_offline_queue');
  ss.del('paystackPending');
  if (logout) {
    if (uid) ls.del(`gcal_token_${uid}`);
    ls.del('gcal_token');
  }
}

// Reload at most MAX_RELOADS_IN_WINDOW times per RELOAD_WINDOW_MS per tab — the
// loop breaker. Returns false when the guard refuses (caller shows a manual
// "reload" screen instead).
export function guardedReload(reload = () => win().location.reload(), now = Date.now()) {
  let log = [];
  try { log = JSON.parse(ss.get(RELOAD_LOG_KEY) || '[]').filter((t) => now - t < RELOAD_WINDOW_MS); } catch (_) { log = []; }
  if (log.length >= MAX_RELOADS_IN_WINDOW) return false;
  ss.set(RELOAD_LOG_KEY, JSON.stringify([...log, now]));
  reload();
  return true;
}

export const stashRecoveryError = (msg) => ss.set(RECOVERY_ERROR_KEY, msg);
export const takeRecoveryError = () => { const m = ss.get(RECOVERY_ERROR_KEY); if (m) ss.del(RECOVERY_ERROR_KEY); return m || ''; };

// Canonical cleanup for LOGOUT / TENANT_CHANGED / FIRM_ACCESS_LOST.
// Precondition: the caller has already left ACTIVE and cleared firm React state.
// deps: { db, terminate, clearIndexedDbPersistence, signOut, clearOfflineCache, clearOfflineQueue, post, reload }
export async function runSessionCleanup(kind, { uid, deps }) {
  const logout = kind === SESSION_EVENTS.LOGOUT;
  try { deps.post(kind); } catch (_) { /* best effort */ }
  clearDeviceTenant();
  try { await deps.clearOfflineCache(); } catch (_) { /* best effort */ }
  if (logout) { try { await deps.clearOfflineQueue(); } catch (_) { /* best effort */ } }
  clearFirmBrowserStorage(uid, { logout });
  const persistence = await clearFirestorePersistence(deps);
  if (logout) { try { await deps.signOut(); } catch (_) { /* reload still lands on sign-in if Auth cleared */ } }
  const reloaded = guardedReload(deps.reload);
  return { persistence, reloaded };
}
